import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, Users, BookOpen, ListChecks, Film, MessagesSquare, KeyRound, Wallet, X, GraduationCap, CalendarCheck, TicketPercent } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import CancelMembershipDialog from "@/components/CancelMembershipDialog";
import TrialBanner from "@/components/TrialBanner";

const adminNav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testid: "nav-admin-dashboard" },
  { to: "/admin/students", label: "Students", icon: Users, testid: "nav-admin-students" },
  { to: "/admin/teachers", label: "Teachers", icon: GraduationCap, testid: "nav-admin-teachers" },
  { to: "/admin/demos", label: "Demos", icon: CalendarCheck, testid: "nav-admin-demos" },
  { to: "/admin/exercises", label: "Exercises", icon: BookOpen, testid: "nav-admin-exercises" },
  { to: "/admin/videos", label: "Videos", icon: Film, testid: "nav-admin-videos" },
  { to: "/admin/community", label: "Community", icon: MessagesSquare, testid: "nav-admin-community" },
  { to: "/admin/results", label: "Results", icon: ListChecks, testid: "nav-admin-results" },
  { to: "/admin/payments", label: "Payments", icon: Wallet, testid: "nav-admin-payments" },
  { to: "/admin/coupons", label: "Coupons", icon: TicketPercent, testid: "nav-admin-coupons" },
];

export function AppHeader({ variant = "student" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pwOpen, setPwOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <>
    <header className="sticky top-0 z-30 border-b border-cloud bg-white/80 backdrop-blur-xl">
      <div className="max-w-6xl mx-auto px-2 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2 sm:gap-4">
        <Link to={variant === "admin" ? "/admin" : "/student"} className="shrink-0">
          <span className="hidden sm:inline-block"><Logo /></span>
          <span className="sm:hidden"><Logo size="sm" /></span>
        </Link>

        {variant === "admin" && (
          <nav className="hidden md:flex items-center gap-1">
            {adminNav.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.to || (item.to !== "/admin" && location.pathname.startsWith(item.to));
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                    active ? "bg-sky text-white shadow-[0_3px_0_0_rgba(0,0,0,0.12)]" : "text-stone hover:bg-sky-light hover:text-sky-hover"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        )}

        <div className="flex items-center gap-1 sm:gap-3">
          {variant === "student" && (
            <>
              <Link
                to="/student/videos"
                data-testid="nav-student-lessons"
                aria-label="Lessons"
                className="inline-flex items-center gap-2 px-2.5 sm:px-4 py-2 rounded-full bg-sky-light text-sky-hover text-sm font-bold hover:bg-sky hover:text-white transition-colors"
              >
                <Film className="w-4 h-4" /> <span className="hidden sm:inline">Lessons</span>
              </Link>
              <Link
                to="/student/community"
                data-testid="nav-student-community"
                aria-label="Community"
                className="inline-flex items-center gap-2 px-2.5 sm:px-4 py-2 rounded-full bg-lemon/40 text-ink text-sm font-bold hover:bg-lemon transition-colors"
              >
                <MessagesSquare className="w-4 h-4" /> <span className="hidden sm:inline">Community</span>
              </Link>
            </>
          )}
          {user && (
            <span data-testid="header-user-name" className="hidden md:block text-sm font-semibold text-ink">
              {user.name}
            </span>
          )}
          <button
            data-testid="change-password-button"
            onClick={() => setPwOpen(true)}
            title="Change password"
            aria-label="Change password"
            className="inline-flex items-center gap-2 px-2.5 py-2 rounded-full bg-canvas text-stone text-sm font-bold hover:bg-cloud hover:text-ink transition-colors"
          >
            <KeyRound className="w-4 h-4" />
            <span className="hidden lg:inline">Password</span>
          </button>
          {variant === "student" && (
            <button
              data-testid="cancel-membership-button"
              onClick={() => setCancelOpen(true)}
              title="Cancel membership"
              aria-label="Cancel membership"
              className="hidden md:inline-flex items-center gap-2 px-2.5 py-2 rounded-full bg-canvas text-stone text-sm font-bold hover:bg-watermelon-light hover:text-watermelon transition-colors"
            >
              <X className="w-4 h-4" />
              <span className="hidden lg:inline">Cancel</span>
            </button>
          )}
          <button
            data-testid="logout-button"
            onClick={onLogout}
            aria-label="Logout"
            className="inline-flex items-center gap-2 px-2.5 sm:px-4 py-2 rounded-full bg-watermelon text-white text-sm font-bold btn-tactile hover:bg-watermelon-hover"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>

      {variant === "admin" && (
        <nav className="md:hidden border-t border-cloud bg-white/95">
          <div className="grid grid-cols-3 gap-1 px-3 py-2">
            {adminNav.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.to || (item.to !== "/admin" && location.pathname.startsWith(item.to));
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`${item.testid}-mobile`}
                  className={`inline-flex flex-col items-center justify-center gap-0.5 px-1 py-2 rounded-xl text-[11px] font-bold text-center leading-tight ${
                    active ? "bg-sky text-white shadow-[0_3px_0_0_rgba(0,0,0,0.10)]" : "text-stone bg-canvas hover:bg-cloud"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </header>
    {/* Rendered outside the header so the backdrop-blur on <header> doesn't capture the fixed overlay */}
    <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    <CancelMembershipDialog open={cancelOpen} onClose={() => setCancelOpen(false)} />
    </>
  );
}

export default function AppLayout({ children, variant = "student" }) {
  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      <AppHeader variant={variant} />
      {variant !== "admin" && <TrialBanner />}
      <main className="flex-1">{children}</main>
    </div>
  );
}
